/**
 * Ferry Pickup — end-of-game animation.
 *
 * One ferry per surviving team spawns from orbit, visits each planet
 * with surviving teammates (multi-stop), picks them up, then flies off.
 *
 * State machine per ferry:
 *   approach → descend → landed → boarding → ascend → [transit → approach...] → depart → done
 */

import * as THREE from 'three';
import { rk4Step, checkCollisionFast } from '../core/physics';
import type { Vec3, PlanetState, TeamPalette } from '../types';
import { buildFerryBody } from './transitModels';
import { G, PHYS_DT, MAX_TERRAIN_HEIGHT_FRACTION } from '../constants';

// ---------------------------------------------------------------------------
// Tuning Constants
// ---------------------------------------------------------------------------

const ENTRY_RADIUS = 60;
const SIM_DT = PHYS_DT * 4;
const MAX_SIM_STEPS = 600;
const NUM_CANDIDATES = 12;
const CLOSE_PASS_MULT = 2.5;
const ARRIVAL_ALT = 4.0;
const PATH_SAMPLES = 200;
const HOMING_FRAC = 0.15;

const APPROACH_DURATION = 3.0;     // seconds for orbital approach
const DESCEND_DURATION = 2.0;      // seconds for descent to surface
const BOARD_WALK_SPEED = 2.5;      // surface distance units/sec for boarding walk
const ASCEND_DURATION = 2.5;       // seconds for liftoff
const DEPART_SPEED = 12.0;         // units/sec during depart phase
const DEPART_DISTANCE = 60;        // distance from centroid to be "done"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface FerryPickupStop {
  planetIndex: number;
  survivorIds: number[];
  walkAnims: Array<{
    playerId: number;
    startDir: Vec3;
    endDir: Vec3;
    progress: number;
    duration: number;
  }>;
}

type FerryState = 'approach' | 'descend' | 'landed' | 'boarding' | 'ascend' | 'transit' | 'depart' | 'done';

export interface FerryPickupEffect {
  rootGroup: THREE.Group;
  flightModel: THREE.Group;
  landedModel: THREE.Group;
  state: FerryState;
  prevState: FerryState;
  stateTime: number;
  teamIndex: number;
  stops: FerryPickupStop[];
  currentStopIndex: number;
  orbitalPath: THREE.Vector3[];
  planetCenter: THREE.Vector3;
  surfaceNormal: THREE.Vector3;
  surfaceRadius: number;
  surfaceQuat: THREE.Quaternion;
  planets: PlanetState[];
  modelScale: number;
  landingDir: Vec3;  // surfaceDir where ferry lands (for boarding walks)
}

export interface FerryUpdateResult {
  running: boolean;
  boardedPlayerIds: number[];
  stateChanged: boolean;
  newState: FerryState;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function smoothstep(t: number): number {
  return t * t * (3 - 2 * t);
}


// ---------------------------------------------------------------------------
// Orbital Approach Path (same pattern as dropCan.ts)
// ---------------------------------------------------------------------------

function computeFerryApproach(
  arrivalPoint: Vec3,
  targetPlanetIndex: number,
  planets: PlanetState[],
): Vec3[] {
  let totalMass = 0;
  let cx = 0, cy = 0, cz = 0;
  for (const p of planets) {
    if (!p.alive) continue;
    totalMass += p.mass;
    cx += p.position.x * p.mass;
    cy += p.position.y * p.mass;
    cz += p.position.z * p.mass;
  }
  if (totalMass > 0) { cx /= totalMass; cy /= totalMass; cz /= totalMass; }
  const centroid: Vec3 = { x: cx, y: cy, z: cz };

  const targetPlanet = planets[targetPlanetIndex];

  let toArrival: Vec3 = {
    x: arrivalPoint.x - centroid.x,
    y: arrivalPoint.y - centroid.y,
    z: arrivalPoint.z - centroid.z,
  };
  const toArrLen = Math.sqrt(toArrival.x ** 2 + toArrival.y ** 2 + toArrival.z ** 2);
  if (toArrLen > 0.001) {
    toArrival = { x: toArrival.x / toArrLen, y: toArrival.y / toArrLen, z: toArrival.z / toArrLen };
  } else {
    toArrival = { x: 1, y: 0, z: 0 };
  }

  const arbitrary: Vec3 = Math.abs(toArrival.y) < 0.9 ? { x: 0, y: 1, z: 0 } : { x: 1, y: 0, z: 0 };
  const basisU: Vec3 = {
    x: toArrival.y * arbitrary.z - toArrival.z * arbitrary.y,
    y: toArrival.z * arbitrary.x - toArrival.x * arbitrary.z,
    z: toArrival.x * arbitrary.y - toArrival.y * arbitrary.x,
  };
  const bULen = Math.sqrt(basisU.x ** 2 + basisU.y ** 2 + basisU.z ** 2);
  if (bULen > 0.001) { basisU.x /= bULen; basisU.y /= bULen; basisU.z /= bULen; }
  const basisV: Vec3 = {
    x: toArrival.y * basisU.z - toArrival.z * basisU.y,
    y: toArrival.z * basisU.x - toArrival.x * basisU.z,
    z: toArrival.x * basisU.y - toArrival.y * basisU.x,
  };

  let bestPath: Vec3[] | null = null;
  let bestScore = -Infinity;

  for (let ci = 0; ci < NUM_CANDIDATES; ci++) {
    const angle = (ci / NUM_CANDIDATES) * Math.PI * 2;
    const cosA = Math.cos(angle);
    const sinA = Math.sin(angle);

    const entryDir: Vec3 = {
      x: cosA * basisU.x + sinA * basisV.x,
      y: cosA * basisU.y + sinA * basisV.y,
      z: cosA * basisU.z + sinA * basisV.z,
    };
    const entryPos: Vec3 = {
      x: centroid.x + entryDir.x * ENTRY_RADIUS,
      y: centroid.y + entryDir.y * ENTRY_RADIUS,
      z: centroid.z + entryDir.z * ENTRY_RADIUS,
    };

    const inwardDir: Vec3 = {
      x: centroid.x - entryPos.x,
      y: centroid.y - entryPos.y,
      z: centroid.z - entryPos.z,
    };
    const inLen = Math.sqrt(inwardDir.x ** 2 + inwardDir.y ** 2 + inwardDir.z ** 2);
    if (inLen > 0.001) { inwardDir.x /= inLen; inwardDir.y /= inLen; inwardDir.z /= inLen; }

    const tangDir: Vec3 = {
      x: entryDir.y * inwardDir.z - entryDir.z * inwardDir.y,
      y: entryDir.z * inwardDir.x - entryDir.x * inwardDir.z,
      z: entryDir.x * inwardDir.y - entryDir.y * inwardDir.x,
    };
    const tLen = Math.sqrt(tangDir.x ** 2 + tangDir.y ** 2 + tangDir.z ** 2);
    if (tLen > 0.001) { tangDir.x /= tLen; tangDir.y /= tLen; tangDir.z /= tLen; }

    const speedFactor = 0.6 + (ci / NUM_CANDIDATES) * 0.7;
    const orbitalSpeed = Math.sqrt(G * totalMass / Math.max(ENTRY_RADIUS, 1)) * speedFactor;

    const toTarget: Vec3 = {
      x: targetPlanet.position.x - entryPos.x,
      y: targetPlanet.position.y - entryPos.y,
      z: targetPlanet.position.z - entryPos.z,
    };
    const ttLen = Math.sqrt(toTarget.x ** 2 + toTarget.y ** 2 + toTarget.z ** 2);
    if (ttLen > 0.001) { toTarget.x /= ttLen; toTarget.y /= ttLen; toTarget.z /= ttLen; }

    const vel: Vec3 = {
      x: tangDir.x * orbitalSpeed * 0.7 + toTarget.x * orbitalSpeed * 0.3,
      y: tangDir.y * orbitalSpeed * 0.7 + toTarget.y * orbitalSpeed * 0.3,
      z: tangDir.z * orbitalSpeed * 0.7 + toTarget.z * orbitalSpeed * 0.3,
    };

    let pos: Vec3 = { ...entryPos };
    let curVel: Vec3 = { ...vel };
    const path: Vec3[] = [{ ...pos }];
    let arrived = false;
    let collided = false;
    const passedPlanets = new Set<number>();

    for (let step = 0; step < MAX_SIM_STEPS; step++) {
      const result = rk4Step(pos, curVel, SIM_DT, planets);
      pos = result.pos;
      curVel = result.vel;
      path.push({ ...pos });

      if (checkCollisionFast(pos, planets)) { collided = true; break; }

      for (let pi = 0; pi < planets.length; pi++) {
        if (!planets[pi].alive || pi === targetPlanetIndex) continue;
        const dx = pos.x - planets[pi].position.x;
        const dy = pos.y - planets[pi].position.y;
        const dz = pos.z - planets[pi].position.z;
        const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
        if (dist < planets[pi].radius * CLOSE_PASS_MULT) passedPlanets.add(pi);
      }

      const ax = pos.x - targetPlanet.position.x;
      const ay = pos.y - targetPlanet.position.y;
      const az = pos.z - targetPlanet.position.z;
      const distFromTarget = Math.sqrt(ax * ax + ay * ay + az * az);
      if (distFromTarget <= targetPlanet.radius + ARRIVAL_ALT) {
        arrived = true;
        break;
      }
    }

    // A path that flew into a planet is never shown, however scenic
    const score = passedPlanets.size * 10 + (arrived ? 50 : 0) - (collided ? 1000 : 0);
    if (score > bestScore) {
      bestScore = score;
      bestPath = path;
    }
  }

  if (!bestPath || bestPath.length < 2) {
    const entryPos: Vec3 = { x: centroid.x + ENTRY_RADIUS, y: centroid.y, z: centroid.z };
    bestPath = [entryPos, arrivalPoint];
  }

  const resampled = resamplePath(bestPath, PATH_SAMPLES);

  const homingStart = Math.floor(PATH_SAMPLES * (1 - HOMING_FRAC));
  for (let i = homingStart; i < resampled.length; i++) {
    const blend = (i - homingStart) / (resampled.length - homingStart);
    resampled[i] = {
      x: resampled[i].x + (arrivalPoint.x - resampled[i].x) * blend,
      y: resampled[i].y + (arrivalPoint.y - resampled[i].y) * blend,
      z: resampled[i].z + (arrivalPoint.z - resampled[i].z) * blend,
    };
  }

  return resampled;
}

/** Clearance a transit leg keeps from any planet it has to route around. */
const TRANSIT_CLEARANCE = 4.0;

/** Closest point on segment ab to c, as the parameter t in [0, 1]. */
function segmentParam(a: Vec3, b: Vec3, c: Vec3): number {
  const abx = b.x - a.x, aby = b.y - a.y, abz = b.z - a.z;
  const len2 = abx * abx + aby * aby + abz * abz;
  if (len2 < 1e-9) return 0;
  const t = ((c.x - a.x) * abx + (c.y - a.y) * aby + (c.z - a.z) * abz) / len2;
  return Math.max(0, Math.min(1, t));
}

/**
 * Waypoints for a ferry leg from `from` to `to` that never cuts through a
 * planet: for each planet whose bounding shell the straight line crosses, the
 * leg is bent around it through a point pushed out from the planet centre.
 * Legs are checked and bent repeatedly until every segment is clear (bounded).
 */
export function computeTransitWaypoints(from: Vec3, to: Vec3, planets: PlanetState[]): Vec3[] {
  const pts: Vec3[] = [from, to];
  for (let pass = 0; pass < 6; pass++) {
    let bent = false;
    for (let i = 0; i < pts.length - 1 && !bent; i++) {
      const a = pts[i], b = pts[i + 1];
      for (const planet of planets) {
        if (!planet.alive) continue;
        const clear = planet.radius * (1 + MAX_TERRAIN_HEIGHT_FRACTION) + TRANSIT_CLEARANCE;
        const t = segmentParam(a, b, planet.position);
        // Endpoints sit just above their own planets; only interior crossings count
        if (t <= 0.02 || t >= 0.98) continue;
        const cx = a.x + (b.x - a.x) * t, cy = a.y + (b.y - a.y) * t, cz = a.z + (b.z - a.z) * t;
        let ox = cx - planet.position.x, oy = cy - planet.position.y, oz = cz - planet.position.z;
        const d = Math.sqrt(ox * ox + oy * oy + oz * oz);
        if (d >= clear) continue;
        if (d < 1e-6) { ox = 0; oy = 1; oz = 0; } else { ox /= d; oy /= d; oz /= d; }
        pts.splice(i + 1, 0, {
          x: planet.position.x + ox * clear,
          y: planet.position.y + oy * clear,
          z: planet.position.z + oz * clear,
        });
        bent = true;
        break;
      }
    }
    if (!bent) break;
  }
  return pts;
}

/** Transit path between stops: the straight line, bent around any planet in the way, with a gentle arc. */
function computeTransitPath(from: Vec3, to: Vec3, planets: PlanetState[]): Vec3[] {
  const waypoints = computeTransitWaypoints(from, to, planets);
  // Densify the polyline before resampling so the detour corners round off
  const dense: Vec3[] = [];
  for (let i = 0; i < waypoints.length - 1; i++) {
    const a = waypoints[i], b = waypoints[i + 1];
    for (let k = 0; k < 20; k++) {
      const t = k / 20;
      dense.push({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t, z: a.z + (b.z - a.z) * t });
    }
  }
  dense.push(waypoints[waypoints.length - 1]);
  const pts = resamplePath(dense, PATH_SAMPLES);
  if (waypoints.length > 2) return pts; // detour legs already have their shape

  // Direct leg: arc slightly above the line, as before
  const dx = to.x - from.x, dy = to.y - from.y, dz = to.z - from.z;
  const len = Math.sqrt(dx * dx + dy * dy + dz * dz);
  let upDir: Vec3 = { x: 0, y: 1, z: 0 };
  if (len > 0.001) {
    const fwd: Vec3 = { x: dx / len, y: dy / len, z: dz / len };
    const worldUp = Math.abs(fwd.y) < 0.9 ? { x: 0, y: 1, z: 0 } : { x: 1, y: 0, z: 0 };
    upDir = {
      x: fwd.y * worldUp.z - fwd.z * worldUp.y,
      y: fwd.z * worldUp.x - fwd.x * worldUp.z,
      z: fwd.x * worldUp.y - fwd.y * worldUp.x,
    };
    const upLen = Math.sqrt(upDir.x ** 2 + upDir.y ** 2 + upDir.z ** 2);
    if (upLen > 0.001) { upDir.x /= upLen; upDir.y /= upLen; upDir.z /= upLen; }
  }
  return pts.map((p, i) => {
    const t = i / (pts.length - 1);
    const arcHeight = 3.0 * Math.sin(t * Math.PI);
    return { x: p.x + upDir.x * arcHeight, y: p.y + upDir.y * arcHeight, z: p.z + upDir.z * arcHeight };
  });
}

function resamplePath(path: Vec3[], n: number): Vec3[] {
  if (path.length < 2) return [{ ...path[0] }];

  const arcLengths: number[] = [0];
  for (let i = 1; i < path.length; i++) {
    const dx = path[i].x - path[i - 1].x;
    const dy = path[i].y - path[i - 1].y;
    const dz = path[i].z - path[i - 1].z;
    arcLengths.push(arcLengths[i - 1] + Math.sqrt(dx * dx + dy * dy + dz * dz));
  }
  const totalLength = arcLengths[arcLengths.length - 1];
  if (totalLength < 0.001) return [{ ...path[0] }];

  const result: Vec3[] = [];
  let segIdx = 0;

  for (let i = 0; i < n; i++) {
    const targetLen = (i / (n - 1)) * totalLength;
    while (segIdx < arcLengths.length - 2 && arcLengths[segIdx + 1] < targetLen) segIdx++;

    const segLen = arcLengths[segIdx + 1] - arcLengths[segIdx];
    const t = segLen > 0.0001 ? (targetLen - arcLengths[segIdx]) / segLen : 0;
    const a = path[segIdx];
    const b = path[segIdx + 1];
    result.push({
      x: a.x + (b.x - a.x) * t,
      y: a.y + (b.y - a.y) * t,
      z: a.z + (b.z - a.z) * t,
    });
  }

  return result;
}

// ---------------------------------------------------------------------------
// Build Ferry Model
// ---------------------------------------------------------------------------

function buildFerryModel(team: TeamPalette, mode: 'flight' | 'landed'): THREE.Group {
  return buildFerryBody(team, mode);
}

function buildFlameGroup(): THREE.Group {
  const flame = new THREE.Group();

  const outerFlame = new THREE.Mesh(
    new THREE.ConeGeometry(0.12, 0.7, 8),
    new THREE.MeshBasicMaterial({ color: '#4488ff', transparent: true, opacity: 0.7 }),
  );
  outerFlame.position.y = 0.35;
  flame.add(outerFlame);

  const innerFlame = new THREE.Mesh(
    new THREE.ConeGeometry(0.05, 0.5, 6),
    new THREE.MeshBasicMaterial({ color: '#aaddff', transparent: true, opacity: 0.9 }),
  );
  innerFlame.position.y = 0.25;
  flame.add(innerFlame);

  // Additive glow blob instead of a PointLight: adding or removing a light makes
  // Three.js recompile every lit material in the scene (a visible hitch at
  // pickup and drop-off). setFlameGlow() drives its opacity and size.
  const glow = new THREE.Mesh(
    new THREE.SphereGeometry(0.3, 10, 8),
    new THREE.MeshBasicMaterial({ color: '#4488ff', transparent: true, opacity: 0, blending: THREE.AdditiveBlending, depthWrite: false }),
  );
  glow.name = 'engineGlow';
  glow.position.y = 0.2;
  flame.add(glow);
  flame.name = 'ferryFlame';
  flame.visible = false;
  return flame;
}

/** Engine glow strength (the old PointLight intensity, 0–4) → blob opacity and size. */
function setFlameGlow(flame: THREE.Object3D, intensity: number): void {
  const glow = flame.getObjectByName('engineGlow') as THREE.Mesh | undefined;
  if (!glow) return;
  (glow.material as THREE.MeshBasicMaterial).opacity = Math.min(0.55, intensity * 0.14);
  glow.scale.setScalar(0.7 + intensity * 0.2);
}

// ---------------------------------------------------------------------------
// Compute surface info for a planet stop
// ---------------------------------------------------------------------------

function computeSurfaceInfo(
  planet: PlanetState,
  landingDir: Vec3,
): { surfaceRadius: number; surfaceNormal: THREE.Vector3; surfaceQuat: THREE.Quaternion } {
  // Find surface height at landing direction
  const bp = planet.basePositions;
  const hm = planet.heightmap;
  let bestDot = -2, bestIdx = 0;
  for (let i = 0; i < hm.length; i++) {
    const vx = bp[i * 3], vy = bp[i * 3 + 1], vz = bp[i * 3 + 2];
    const d = vx * landingDir.x + vy * landingDir.y + vz * landingDir.z;
    if (d > bestDot) { bestDot = d; bestIdx = i; }
  }
  const surfaceRadius = planet.radius + hm[bestIdx];
  const surfaceNormal = new THREE.Vector3(landingDir.x, landingDir.y, landingDir.z);
  const surfaceQuat = new THREE.Quaternion().setFromUnitVectors(
    new THREE.Vector3(0, 1, 0),
    surfaceNormal,
  );
  return { surfaceRadius, surfaceNormal, surfaceQuat };
}

// ---------------------------------------------------------------------------
// Create Ferry Pickup Effect
// ---------------------------------------------------------------------------

export function createFerryPickupEffect(
  scene: THREE.Scene,
  teamIndex: number,
  teamPalette: TeamPalette,
  survivors: Array<{ id: number; planetIndex: number; surfaceDir: Vec3 }>,
  planets: PlanetState[],
): FerryPickupEffect {
  // Group survivors by planet
  const byPlanet = new Map<number, Array<{ id: number; surfaceDir: Vec3 }>>();
  for (const s of survivors) {
    if (!byPlanet.has(s.planetIndex)) byPlanet.set(s.planetIndex, []);
    byPlanet.get(s.planetIndex)!.push({ id: s.id, surfaceDir: s.surfaceDir });
  }

  // Build stops, order by proximity to first planet
  const stopPlanets = [...byPlanet.keys()];
  const stops: FerryPickupStop[] = [];
  for (const pi of stopPlanets) {
    const group = byPlanet.get(pi)!;
    stops.push({
      planetIndex: pi,
      survivorIds: group.map(g => g.id),
      walkAnims: [],  // populated when boarding starts
    });
  }

  // Simple greedy ordering by nearest neighbor
  if (stops.length > 1) {
    const ordered: FerryPickupStop[] = [stops[0]];
    const remaining = stops.slice(1);
    while (remaining.length > 0) {
      const lastPi = ordered[ordered.length - 1].planetIndex;
      const lastPos = planets[lastPi].position;
      let bestIdx = 0;
      let bestDist = Infinity;
      for (let i = 0; i < remaining.length; i++) {
        const pos = planets[remaining[i].planetIndex].position;
        const dx = pos.x - lastPos.x, dy = pos.y - lastPos.y, dz = pos.z - lastPos.z;
        const d = dx * dx + dy * dy + dz * dz;
        if (d < bestDist) { bestDist = d; bestIdx = i; }
      }
      ordered.push(remaining.splice(bestIdx, 1)[0]);
    }
    stops.length = 0;
    stops.push(...ordered);
  }

  // Build models
  const flightModel = buildFerryModel(teamPalette, 'flight');
  const landedModel = buildFerryModel(teamPalette, 'landed');
  const flame = buildFlameGroup();

  // First stop setup
  const firstPlanet = planets[stops[0].planetIndex];
  // Use average survivor direction as landing direction
  const avgDir = averageDirection(byPlanet.get(stops[0].planetIndex)!.map(s => s.surfaceDir));
  const { surfaceRadius, surfaceNormal, surfaceQuat } = computeSurfaceInfo(firstPlanet, avgDir);

  const modelScale = surfaceRadius * 0.4;

  const rootGroup = new THREE.Group();

  const flightContainer = new THREE.Group();
  flightContainer.scale.setScalar(modelScale);
  flightContainer.add(flightModel);
  flightContainer.add(flame);
  rootGroup.add(flightContainer);

  const landedContainer = new THREE.Group();
  landedContainer.scale.setScalar(modelScale);
  landedContainer.add(landedModel);
  landedContainer.visible = false;
  rootGroup.add(landedContainer);

  // Compute orbital approach to first planet
  const arrivalPoint: Vec3 = {
    x: firstPlanet.position.x + avgDir.x * (surfaceRadius + ARRIVAL_ALT),
    y: firstPlanet.position.y + avgDir.y * (surfaceRadius + ARRIVAL_ALT),
    z: firstPlanet.position.z + avgDir.z * (surfaceRadius + ARRIVAL_ALT),
  };
  const pathVec3 = computeFerryApproach(arrivalPoint, stops[0].planetIndex, planets);
  const orbitalPath = pathVec3.map(p => new THREE.Vector3(p.x, p.y, p.z));

  // Start at path beginning
  if (orbitalPath.length > 0) {
    rootGroup.position.copy(orbitalPath[0]);
  }
  scene.add(rootGroup);

  return {
    rootGroup,
    flightModel: flightContainer,
    landedModel: landedContainer,
    state: 'approach',
    prevState: 'approach',
    stateTime: 0,
    teamIndex,
    stops,
    currentStopIndex: 0,
    orbitalPath,
    planetCenter: new THREE.Vector3(firstPlanet.position.x, firstPlanet.position.y, firstPlanet.position.z),
    surfaceNormal,
    surfaceRadius,
    surfaceQuat,
    planets,
    modelScale,
    landingDir: avgDir,
  };
}

function averageDirection(dirs: Vec3[]): Vec3 {
  let ax = 0, ay = 0, az = 0;
  for (const d of dirs) { ax += d.x; ay += d.y; az += d.z; }
  const len = Math.sqrt(ax * ax + ay * ay + az * az);
  if (len < 0.001) return dirs[0];
  return { x: ax / len, y: ay / len, z: az / len };
}

// ---------------------------------------------------------------------------
// Update Ferry Pickup Effect
// ---------------------------------------------------------------------------

export function updateFerryPickupEffect(
  effect: FerryPickupEffect,
  dt: number,
  time: number,
): FerryUpdateResult {
  effect.prevState = effect.state;
  effect.stateTime += dt;
  const st = effect.stateTime;
  const boardedPlayerIds: number[] = [];

  switch (effect.state) {
    case 'approach': {
      const rawT = Math.min(st / APPROACH_DURATION, 1);
      const t = smoothstep(rawT);

      const pathLen = effect.orbitalPath.length;
      const pathT = t * (pathLen - 1);
      const idx = Math.min(Math.floor(pathT), pathLen - 2);
      const frac = pathT - idx;

      const a = effect.orbitalPath[idx];
      const b = effect.orbitalPath[idx + 1];
      const pos = new THREE.Vector3(
        a.x + (b.x - a.x) * frac,
        a.y + (b.y - a.y) * frac,
        a.z + (b.z - a.z) * frac,
      );
      effect.rootGroup.position.copy(pos);

      // Orient along path tangent
      const nextIdx = Math.min(idx + 1, pathLen - 1);
      const tang = new THREE.Vector3().subVectors(effect.orbitalPath[nextIdx], a);
      if (tang.lengthSq() > 0.0001) {
        tang.normalize();
        const lookTarget = new THREE.Vector3().copy(pos).add(tang);
        effect.flightModel.lookAt(lookTarget);
      }

      // Last 20%: slerp toward landing orientation
      if (rawT > 0.8) {
        const blend = (rawT - 0.8) / 0.2;
        effect.flightModel.quaternion.slerp(effect.surfaceQuat, blend);
      }

      // Show flame
      const flame = effect.flightModel.getObjectByName('ferryFlame') as THREE.Group | undefined;
      if (flame) {
        flame.visible = true;
        setFlameGlow(flame, 2.0);
      }

      effect.flightModel.visible = true;
      effect.landedModel.visible = false;

      if (rawT >= 1) {
        effect.state = 'descend';
        effect.stateTime = 0;
        effect.flightModel.quaternion.copy(effect.surfaceQuat);
      }
      break;
    }

    case 'descend': {
      const rawT = Math.min(st / DESCEND_DURATION, 1);
      const t = 1 - (1 - rawT) * (1 - rawT); // ease-out

      const startHeight = ARRIVAL_ALT;
      const endHeight = 0.22 * effect.modelScale;
      const currentHeight = startHeight + (endHeight - startHeight) * t;

      const pos = new THREE.Vector3()
        .copy(effect.planetCenter)
        .addScaledVector(effect.surfaceNormal, effect.surfaceRadius + currentHeight);

      effect.rootGroup.position.copy(pos);
      effect.flightModel.quaternion.copy(effect.surfaceQuat);

      // Subtle sway
      const swayQuat = new THREE.Quaternion().setFromEuler(
        new THREE.Euler(Math.sin(time * 1.8) * 0.02, 0, Math.cos(time * 2.1) * 0.02),
      );
      effect.flightModel.quaternion.multiply(swayQuat);

      // Flame diminish
      const flame = effect.flightModel.getObjectByName('ferryFlame') as THREE.Group | undefined;
      if (flame) {
        flame.visible = true;
        const dist = (currentHeight - endHeight) / startHeight;
        setFlameGlow(flame, dist * 3.0);
        for (const child of flame.children) {
          if (child instanceof THREE.Mesh) {
            const s = 0.3 + dist * 0.7;
            child.scale.setScalar(s);
          }
        }
      }

      effect.flightModel.visible = true;
      effect.landedModel.visible = false;

      if (rawT >= 1) {
        effect.state = 'landed';
        effect.stateTime = 0;

        // Swap to landed model
        effect.flightModel.visible = false;
        effect.landedModel.visible = true;
        effect.landedModel.quaternion.copy(effect.surfaceQuat);

        // Position on surface
        const landPos = new THREE.Vector3()
          .copy(effect.planetCenter)
          .addScaledVector(effect.surfaceNormal, effect.surfaceRadius);
        effect.rootGroup.position.copy(landPos);

        // Hide flame
        if (flame) flame.visible = false;
      }
      break;
    }

    case 'landed': {
      // Brief pause (0.5s), then start boarding
      if (st > 0.5) {
        effect.state = 'boarding';
        effect.stateTime = 0;

        // Set up walk anims for this stop's survivors
        const stop = effect.stops[effect.currentStopIndex];
        stop.walkAnims = [];
        for (const sid of stop.survivorIds) {
          // Walk FROM their current surfaceDir TO the ferry's landingDir
          // (we don't have their surfaceDir stored in stops, so main.ts
          //  handles the actual walk mesh updates — we just track progress)
          const arcDist = 0.5; // approximate; actual walk is handled by main.ts
          const duration = Math.max(0.4, arcDist / BOARD_WALK_SPEED);
          stop.walkAnims.push({
            playerId: sid,
            startDir: { x: 0, y: 0, z: 0 }, // placeholder, main.ts uses player's real surfaceDir
            endDir: effect.landingDir,
            progress: 0,
            duration,
          });
        }
      }
      break;
    }

    case 'boarding': {
      const stop = effect.stops[effect.currentStopIndex];
      let allBoarded = true;

      for (const anim of stop.walkAnims) {
        if (anim.progress >= 1) continue;

        anim.progress += dt / anim.duration;
        if (anim.progress >= 1) {
          anim.progress = 1;
          boardedPlayerIds.push(anim.playerId);
        } else {
          allBoarded = false;
        }
      }

      if (allBoarded && stop.walkAnims.length > 0) {
        // All survivors boarded — ascend
        effect.state = 'ascend';
        effect.stateTime = 0;
      } else if (stop.walkAnims.length === 0) {
        // No survivors at this stop (edge case)
        effect.state = 'ascend';
        effect.stateTime = 0;
      }
      break;
    }

    case 'ascend': {
      const rawT = Math.min(st / ASCEND_DURATION, 1);
      const t = rawT * rawT; // ease-in (accelerating up)

      const endHeight = 0.22 * effect.modelScale;
      const currentHeight = endHeight + (ARRIVAL_ALT + 2.0 - endHeight) * t;

      const pos = new THREE.Vector3()
        .copy(effect.planetCenter)
        .addScaledVector(effect.surfaceNormal, effect.surfaceRadius + currentHeight);

      effect.rootGroup.position.copy(pos);

      // Switch back to flight model
      effect.flightModel.visible = true;
      effect.landedModel.visible = false;
      effect.flightModel.quaternion.copy(effect.surfaceQuat);

      // Show flame during ascent
      const flame = effect.flightModel.getObjectByName('ferryFlame') as THREE.Group | undefined;
      if (flame) {
        flame.visible = true;
        setFlameGlow(flame, 2.0 + rawT * 1.0);
      }

      if (rawT >= 1) {
        // More stops?
        if (effect.currentStopIndex < effect.stops.length - 1) {
          effect.state = 'transit';
          effect.stateTime = 0;
        } else {
          effect.state = 'depart';
          effect.stateTime = 0;
        }
      }
      break;
    }

    case 'transit': {
      // Move to next stop
      effect.currentStopIndex++;
      const nextStop = effect.stops[effect.currentStopIndex];
      const nextPlanet = effect.planets[nextStop.planetIndex];
      const nextDir = averageDirection(
        nextStop.survivorIds.map(() => ({ x: 0, y: 1, z: 0 })),  // placeholder
      );
      // Use the planet's "up" as landing dir (first survivor's direction would be better but we don't store it)
      const planetDir: Vec3 = {
        x: nextPlanet.position.x,
        y: nextPlanet.position.y,
        z: nextPlanet.position.z,
      };
      const pLen = Math.sqrt(planetDir.x ** 2 + planetDir.y ** 2 + planetDir.z ** 2);
      const normalizedDir: Vec3 = pLen > 0.001
        ? { x: planetDir.x / pLen, y: planetDir.y / pLen, z: planetDir.z / pLen }
        : { x: 0, y: 1, z: 0 };

      effect.landingDir = normalizedDir;
      const info = computeSurfaceInfo(nextPlanet, normalizedDir);
      effect.surfaceRadius = info.surfaceRadius;
      effect.surfaceNormal = info.surfaceNormal;
      effect.surfaceQuat = info.surfaceQuat;
      effect.planetCenter = new THREE.Vector3(nextPlanet.position.x, nextPlanet.position.y, nextPlanet.position.z);

      // Compute transit path
      const fromPos = effect.rootGroup.position;
      const arrivalPoint: Vec3 = {
        x: nextPlanet.position.x + normalizedDir.x * (info.surfaceRadius + ARRIVAL_ALT),
        y: nextPlanet.position.y + normalizedDir.y * (info.surfaceRadius + ARRIVAL_ALT),
        z: nextPlanet.position.z + normalizedDir.z * (info.surfaceRadius + ARRIVAL_ALT),
      };
      const transitVec3 = computeTransitPath(
        { x: fromPos.x, y: fromPos.y, z: fromPos.z },
        arrivalPoint,
        effect.planets,
      );
      effect.orbitalPath = transitVec3.map(p => new THREE.Vector3(p.x, p.y, p.z));

      effect.state = 'approach';
      effect.stateTime = 0;
      break;
    }

    case 'depart': {
      // Fly away from system centroid
      let cx = 0, cy = 0, cz = 0, totalMass = 0;
      for (const p of effect.planets) {
        if (!p.alive) continue;
        totalMass += p.mass;
        cx += p.position.x * p.mass;
        cy += p.position.y * p.mass;
        cz += p.position.z * p.mass;
      }
      if (totalMass > 0) { cx /= totalMass; cy /= totalMass; cz /= totalMass; }

      const pos = effect.rootGroup.position;
      let awayDir = new THREE.Vector3(pos.x - cx, pos.y - cy, pos.z - cz);
      if (awayDir.lengthSq() < 0.001) awayDir.set(1, 0, 0);
      awayDir.normalize();

      pos.addScaledVector(awayDir, DEPART_SPEED * dt);

      // Orient along departure direction
      const lookTarget = new THREE.Vector3().copy(pos).add(awayDir);
      effect.flightModel.lookAt(lookTarget);

      effect.flightModel.visible = true;
      effect.landedModel.visible = false;

      // Show flame
      const flame = effect.flightModel.getObjectByName('ferryFlame') as THREE.Group | undefined;
      if (flame) {
        flame.visible = true;
        setFlameGlow(flame, 3.0);
      }

      // Check distance
      const dist = Math.sqrt((pos.x - cx) ** 2 + (pos.y - cy) ** 2 + (pos.z - cz) ** 2);
      if (dist > DEPART_DISTANCE) {
        effect.state = 'done';
      }
      break;
    }

    case 'done':
      return { running: false, boardedPlayerIds, stateChanged: effect.prevState !== 'done', newState: 'done' };
  }

  const stateChanged = effect.prevState !== effect.state;
  return { running: true, boardedPlayerIds, stateChanged, newState: effect.state };
}

// ---------------------------------------------------------------------------
// Dispose
// ---------------------------------------------------------------------------

export function disposeFerryPickupEffect(scene: THREE.Scene, effect: FerryPickupEffect): void {
  effect.rootGroup.traverse(obj => {
    if (obj instanceof THREE.Mesh) {
      obj.geometry.dispose();
      if (Array.isArray(obj.material)) {
        obj.material.forEach(m => m.dispose());
      } else {
        obj.material.dispose();
      }
    }
    if (obj instanceof THREE.PointLight) {
      obj.dispose();
    }
  });
  scene.remove(effect.rootGroup);
}
